/**
=========================================================
* Trust Cargo - Orders (the Follow up sheet)
=========================================================

* Reads the v2 database, not the legacy one.
*
* This page used to call /api/orders, which returns {"orders":[]} - the
* legacy table is empty, so the screen was blank no matter what. Orders now
* live in v2 (589 of them, imported from the workbook), and that is also
* where the create dialog writes, so a new order shows up here immediately.
*
* Money columns are derived from the payments ledger, never typed - Deposit
* and payment are SUMs, Balance is final_amount minus what was paid.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import axios from "axios";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";
import ensureSupplierForShopCode from "utils/suppliers";
import { boardAction } from "utils/board";
import fetchFirstAmounts from "utils/orderAmounts";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === "" ? "-" : Number(v).toLocaleString("en-US");

function Orders() {
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // ids ticked in the table, kept as a Set for O(1) lookups while rendering
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  // column filters, all applied on top of the free-text search
  const [status, setStatus] = useState("");
  const [container, setContainer] = useState("");
  const [customer, setCustomer] = useState("");
  const [settled, setSettled] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      // the endpoint defaults to a small page; ask for the lot and let the
      // client-side pager and search work across all of it
      const res = await fetch(`${API}/orders?limit=2000`);
      const json = await res.json();
      const rows = Array.isArray(json) ? json : [];

      /**
       * first_amount drives the Expected Balance column but is not on the
       * list response, so it is recovered per order until the server sends
       * it. Orders that already carry it cost nothing.
       */
      const firstById = await fetchFirstAmounts(rows);

      // null rather than undefined, so the column can tell "not known" from
      // a genuine zero
      const firstFor = (o) => {
        if (o.first_amount !== undefined && o.first_amount !== null) return Number(o.first_amount);
        return firstById.has(o.id) ? firstById.get(o.id) : null;
      };

      setOrders(rows.map((o) => ({ ...o, first_amount: firstFor(o) })));
    } catch (e) {
      setOrders([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Column filters first, then the free-text search over what is left, so
   * the search box narrows the visible tab rather than the whole table.
   */
  const narrowed = orders.filter((o) => {
    if (!matches(o.status, status)) return false;
    if (!matches(o.container, container)) return false;
    if (!matches(o.customer, customer)) return false;
    if (!inDateRange(o.date, from, to)) return false;

    if (settled === "unpaid" && !(Number(o.balance) > 0)) return false;
    if (settled === "settled" && Number(o.balance) > 0) return false;
    if (settled === "overpaid" && !(Number(o.balance) < 0)) return false;

    return true;
  });

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  // totals of what is on screen, so a filtered view still answers "how much"
  const shown = filteredRows.reduce(
    (acc, r) => ({
      charged: acc.charged + (Number(r.charged) || 0),
      paid: acc.paid + (Number(r.paid) || 0),
      balance: acc.balance + (Number(r.balance) || 0),
    }),
    { charged: 0, paid: 0, balance: 0 }
  );

  const cell = { fontSize: 14, whiteSpace: "nowrap" };

  // select-all applies to the CURRENT page only - ticking a header box and
  // silently selecting 589 hidden rows is how people delete things by mistake
  const pageIds = pager.pageRows.map((o) => o.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  /**
   * Copy an order. The server assigns the next code and the customer's next
   * shop code, and deliberately does not copy the payments - that money
   * belongs to the original order.
   */
  const duplicateOne = async (o) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/orders/${o.id}/duplicate`);

      // the copy gets a new shop code, so it needs its supplier too
      const newCode = res?.data?.order?.shop_code;
      if (newCode) await ensureSupplierForShopCode(newCode);

      await fetchData();
      // eslint-disable-next-line no-alert
      window.alert(res?.data?.message || "Duplicated");
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not duplicate");
    }
    setBusy(false);
  };

  const deleteOne = async (o) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete order ${o.shop_code} (code ${o.code})?`)) return;

    setBusy(true);
    try {
      await axios.delete(`${API}/orders/${o.id}`);
      await fetchData();
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(o.id);
        return next;
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} order(s)?`)) return;

    setBusy(true);
    try {
      await axios.post(`${API}/orders/bulk-delete`, { ids });
      setSelected(new Set());
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
    setBusy(false);
  };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          {/* title, search, totals, count and the button - one row */}
          <FilterBar
            title="Orders"
            value={query}
            onChange={setQuery}
            count={filteredRows.length}
            total={orders.length}
            placeholder="Search by shop code, customer or container..."
            actions={
              <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                Add Order
              </Button>
            }
          >
            {/* the choices come from the rows themselves, so the list can
                never offer a status or container that does not exist */}
            <FilterSelect
              label="Status"
              icon="flag"
              value={status}
              onChange={setStatus}
              options={optionsFromRows(orders, "status")}
            />
            <FilterSelect
              label="Container"
              icon="inventory_2"
              value={container}
              onChange={setContainer}
              options={optionsFromRows(orders, "container")}
            />
            <FilterSelect
              label="Customer"
              icon="person"
              value={customer}
              onChange={setCustomer}
              options={optionsFromRows(orders, "customer")}
            />
            <FilterSelect
              label="Money"
              icon="payments"
              value={settled}
              onChange={setSettled}
              anyLabel="All"
              options={[
                { value: "unpaid", label: "Outstanding" },
                { value: "settled", label: "Settled" },
                { value: "overpaid", label: "Overpaid" },
              ]}
            />
            <FilterDates label="Ordered" from={from} to={to} onFrom={setFrom} onTo={setTo} />

            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {`Charged ${money(shown.charged)} · Paid ${money(shown.paid)} · Balance ${money(
                shown.balance
              )}`}
            </SuiTypography>
          </FilterBar>

          {/* only appears once something is ticked, so it never takes up
              space during normal browsing */}
          {selected.size > 0 && (
            <SuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
              mx={3}
              mb={2}
              px={2}
              py={1.25}
              sx={({ palette, borders, functions: { rgba } }) => ({
                borderRadius: borders.borderRadius.md,
                backgroundColor: rgba(palette.primary.main, 0.12),
                border: `1px solid ${rgba(palette.primary.main, 0.35)}`,
              })}
            >
              <SuiTypography variant="button" fontWeight="bold">
                {`${selected.size} selected`}
              </SuiTypography>

              <SuiBox display="flex" gap={1}>
                <Button size="small" onClick={() => setSelected(new Set())} disabled={busy}>
                  Clear selection
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={deleteSelected}
                  disabled={busy}
                >
                  Delete selected
                </Button>
              </SuiBox>
            </SuiBox>
          )}

          {loading ? (
            <CargoLoader label="Loading orders" />
          ) : (
            <>
              <SuiBox sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 1280 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" padding="checkbox">
                        <Checkbox
                          checked={allOnPageSelected}
                          indeterminate={
                            !allOnPageSelected && pageIds.some((id) => selected.has(id))
                          }
                          onChange={togglePage}
                        />
                      </TableCell>
                      <IdHead />
                      <TableCell align="center">CODE</TableCell>
                      <TableCell align="center">shop code</TableCell>
                      <TableCell align="center">Customer</TableCell>
                      <TableCell align="center">Order Date</TableCell>
                      <TableCell align="center">Type of goods</TableCell>
                      <TableCell align="center">CON NO</TableCell>
                      <TableCell align="center">STATUS</TableCell>
                      <TableCell align="center">Final Amount</TableCell>
                      <TableCell align="center">Deposit</TableCell>
                      <TableCell align="center">payment</TableCell>
                      {/* the quoted figure first, then the agreed one */}
                      <TableCell align="center">Expected Balance</TableCell>
                      <TableCell align="center">Balance</TableCell>
                      <TableCell align="center">Operations</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pager.pageRows.map((o) => (
                      <TableRow key={o.id} selected={selected.has(o.id)}>
                        <TableCell align="center" padding="checkbox">
                          <Checkbox checked={selected.has(o.id)} onChange={() => toggleRow(o.id)} />
                        </TableCell>
                        <IdCell value={o.id} />
                        <TableCell align="center" sx={cell}>
                          {o.code ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                          {o.shop_code ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.customer ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.date ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.goods ?? "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {o.container ?? "-"}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {o.status ? (
                            <Chip
                              label={o.status}
                              size="small"
                              sx={({ palette, functions: { rgba } }) => ({
                                fontWeight: 700,
                                backgroundColor: rgba(palette.primary.main, 0.16),
                                color: palette.dark.main,
                              })}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          {money(o.charged)}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {money(o.deposit)}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {money(o.paid)}
                        </TableCell>

                        {/* what was expected before the amount was agreed:
                            the first amount less the deposit taken. The
                            supplier column is the sum of these, so the two
                            screens have to net off the same thing. */}
                        <TableCell align="center" sx={{ ...cell, opacity: 0.85 }}>
                          {o.first_amount === null || o.first_amount === undefined
                            ? "-"
                            : money(Number(o.first_amount) - (Number(o.deposit) || 0))}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={({ palette }) => ({
                            ...cell,
                            fontWeight: 700,
                            // a negative balance means the customer overpaid
                            color: Number(o.balance) < 0 ? palette.error.main : palette.dark.main,
                          })}
                        >
                          {money(o.balance)}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          <RowActions
                            actions={[
                              boardAction("order", o, fetchData),
                              {
                                icon: "edit",
                                label: "Edit order",
                                onClick: () => setEditingId(o.id),
                              },
                              {
                                icon: "content_copy",
                                label: "Duplicate order",
                                tone: "primary",
                                disabled: busy,
                                onClick: () => duplicateOne(o),
                              },
                              {
                                icon: "delete",
                                label: "Delete order",
                                tone: "danger",
                                disabled: busy,
                                onClick: () => deleteOne(o),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            No orders
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </SuiBox>
              <Pagination {...pager} />
            </>
          )}
        </Card>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="order"
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* same dialog, loaded with an existing order */}
      <AddDialog
        open={Boolean(editingId)}
        entity="order"
        recordId={editingId}
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Orders;
